/**
 * Recording a fact.
 *
 * Everything that changes anything goes through here: validate, append to the local log, apply
 * to local projections immediately, queue for the server. The UI updates from the projection,
 * not from the network — which is what makes the app feel instant and what makes it keep
 * working when there is no signal at all.
 */

import { applyEvent } from '@abastece/core';
import { EventValidationError, newId, parseEvent, type EventType, type PayloadInputOf } from '@abastece/shared';
import { registrarYAplicar } from '../db/aplicar.js';
import { getMeta } from '../db/dexie.js';
import { localReader } from '../db/reader.js';
import { refrescarContadores, sincronizar } from '../sync/engine.js';
import { traducir } from '../i18n/index.js';
import { etiquetaCampo } from '../i18n/campos.js';

export class NoRegistradoError extends Error {
  constructor() {
    super(traducir('error.sinRegistrar'));
    this.name = 'NoRegistradoError';
  }
}

export class SinSesionError extends Error {
  constructor() {
    super(traducir('error.sinSesion'));
    this.name = 'SinSesionError';
  }
}

export async function emitir<T extends EventType>(
  type: T,
  payload: PayloadInputOf<T>,
  opts: { actorId?: string } = {},
): Promise<string> {
  return (await registrar(type, payload, opts)).id;
}

/**
 * Like `emitir`, and says whether this device's books took it: the reducer's reason when they
 * did not. For a screen that must not carry on as if it had worked — a sale that clears the cart,
 * say — and so says it did not: a refused event then never happened, is kept in the log as refused,
 * and is never sent (`registrarYAplicar`), so the server cannot take it after all.
 */
export async function emitirYVer<T extends EventType>(
  type: T,
  payload: PayloadInputOf<T>,
  opts: { actorId?: string } = {},
): Promise<{ id: string; rechazo: string | null }> {
  const { id, rechazos } = await registrar(type, payload, { ...opts, siSeRechazaNoVa: true });
  return { id, rechazo: rechazos[0]?.reason ?? null };
}

/**
 * What this device's books would say to an event, recording nothing: the reducer's reason for
 * refusing it, or null if they would take it. For a sheet that should say «ya no se puede» when it
 * opens, before anybody types a reason and a PIN for nothing; the event itself, sent after, still
 * has the last word. Only reads: the reducers return what to write, and nothing here writes it.
 */
export async function probarEvento<T extends EventType>(type: T, payload: PayloadInputOf<T>): Promise<string | null> {
  const [deviceId, actorId, deviceSeq] = await Promise.all([getMeta('dispositivoId'), getMeta('usuarioActivoId'), getMeta('proximoDeviceSeq')]);
  if (!deviceId || !actorId) return null;
  try {
    // The number the event itself will get: its place in the log is where it will land.
    const event = parseEvent({ id: newId(), type, payload, deviceId, deviceSeq: deviceSeq ?? 1, actorId, clientTs: new Date().toISOString(), clockOffsetMs: 0 });
    return (await applyEvent(event, localReader)).rejections[0]?.reason ?? null;
  } catch {
    // Whatever stopped the try stops the real thing too, and says so there.
    return null;
  }
}

async function registrar<T extends EventType>(
  type: T,
  payload: PayloadInputOf<T>,
  opts: { actorId?: string; siSeRechazaNoVa?: boolean },
): Promise<{ id: string; rechazos: readonly { reason: string }[] }> {
  const dispositivoId = await getMeta('dispositivoId');
  if (!dispositivoId) throw new NoRegistradoError();

  const actorId = opts.actorId ?? (await getMeta('usuarioActivoId'));
  if (!actorId) throw new SinSesionError();

  const clockOffsetMs = (await getMeta('clockOffsetMs')) ?? 0;

  const envelope = {
    id: newId(),
    type,
    payload,
    deviceId: dispositivoId,
    actorId,
    clientTs: new Date().toISOString(),
    clockOffsetMs,
  };

  // Numbered, validated, stored and applied in one go (`registrarYAplicar`).
  let rechazos: readonly { reason: string }[];
  try {
    rechazos = await registrarYAplicar(envelope, { siSeRechazaNoVa: opts.siSeRechazaNoVa ?? false });
  } catch (e) {
    // A text past what its field takes, said in words: which one, and how long it may be.
    if (e instanceof EventValidationError && e.largo) {
      throw new Error(traducir('error.textoLargo', { campo: etiquetaCampo(e.largo.campo, type), maximo: e.largo.maximo }));
    }
    throw e;
  }

  await refrescarContadores();
  void sincronizar();

  return { id: envelope.id, rechazos };
}
